import { API_URL } from '@/config'

export class AdminApiError extends Error {
	constructor(
		public status: number,
		message: string,
	) {
		super(message)
	}
}

async function request<T>(path: string, method = 'GET', body?: unknown): Promise<T> {
	const res = await fetch(`${API_URL}/api/admin${path}`, {
		method,
		credentials: 'include',
		headers: { 'Content-Type': 'application/json' },
		body: body !== undefined ? JSON.stringify(body) : undefined,
	})

	if (!res.ok) {
		let message = `Request failed with ${res.status}`
		try {
			const body = (await res.json()) as { message?: string }
			if (body?.message) message = body.message
		} catch {}
		throw new AdminApiError(res.status, message)
	}

	return (await res.json()) as T
}

export type AdminMe = {
	id: string
	email: string
	name: string | null
	username: string | null
	image: string | null
	emailVerified: boolean
	createdAt: string
	updatedAt: string
	providers: string[]
	session: {
		createdAt: string
		expiresAt: string
		ipAddress: string | null
		userAgent: string | null
	}
}

export type AdminStats = {
	total: number
	connected: number
	pending: number
	createdLast7Days: number
}

export type AdminBranch = {
	id: number
	branchId: number
	branchName: string | null
	ico: string
	departmentId: number | null
	fb_ipaddress: string | null
	setupAt: string | null
	createdAt: string
	updatedAt: string
}

export type AdminBranchDetail = {
	id: number
	branchId: number
	branchName: string | null
	ico: string
	departmentId: number | null
	fb_ipaddress: string | null
	fb_port: number | null
	fb_databasePathName: string | null
	fb_username: string | null
	fb_password: string | null
	setupAt: string | null
	createdAt: string
	updatedAt: string
}

export type TestConnectionResult = {
	success: boolean
	url: string
	latencyMs: number | null
	error: string | null
	testedAt: string
}

export type UpdateBranchInput = {
	branchName?: string
	ico?: string
	departmentId?: number
	fb_ipaddress?: string
	fb_port?: number
	fb_databasePathName?: string
	fb_username?: string
	fb_password?: string
}

export const adminApi = {
	me: () => request<AdminMe>('/me'),
	stats: () => request<AdminStats>('/stats'),
	branches: () => request<AdminBranch[]>('/branches'),
	branch: (branchId: number) => request<AdminBranchDetail>(`/branches/${branchId}`),
	updateBranch: (branchId: number, data: UpdateBranchInput) =>
		request<AdminBranchDetail>(`/branches/${branchId}`, 'PUT', data),
	testConnection: (branchId: number) =>
		request<TestConnectionResult>(`/branches/${branchId}/test-connection`, 'POST'),
}
